// TrainPods console shell (Oct 2026 redesign, same system as customer-portal).
// Three widths:
//   > 1100px  full 232px sidebar with group label
//   821-1100  76px icon rail
//   <= 820px  hidden; the mobile bar's menu button opens it full-width
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { useWalletModal } from '../../store/walletModal';
import { walletApi, builderCreditsApi } from '../../services/platformApi';
import WalletModal from '../WalletModal';
import ProductSwitcher from '../ProductSwitcher';
import { BuilderCreditsBanner } from '@podstack/ui';
import { SIDEBAR_NAV } from '../../config/consoleNav';
import { formatINR } from '../console/ui';
import { CompassIc, MenuIc, CloseIc, LogoutIc } from '../console/icons';


const itemBase =
  'flex items-center gap-2.5 min-h-[32px] px-2.5 rounded-md text-sm transition-colors ' +
  `min-[821px]:max-[1100px]:justify-center min-[821px]:max-[1100px]:w-12 min-[821px]:max-[1100px]:min-h-[44px] min-[821px]:max-[1100px]:px-0 ` +
  'max-[820px]:min-h-[44px] max-[820px]:text-[15px]';
const labelCls = `min-[821px]:max-[1100px]:sr-only truncate`;

const useWallet = () =>
  useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletApi.getWallet().then((r) => r.data),
    staleTime: 30000,
  });

/** Wallet balance button: opens the shared wallet modal. */
const WalletButton: React.FC<{ onOpen?: () => void; className?: string }> = ({ onOpen, className = '' }) => {
  const showWallet = useWalletModal((s) => s.show);
  const walletQ = useWallet();
  const balance = Number(walletQ.data?.available_balance ?? walletQ.data?.balance ?? 0);
  return (
    <button
      type="button"
      onClick={() => {
        onOpen?.();
        showWallet();
      }}
      className={`ps-btn2 ${balance < 0 ? 'text-ps-err' : ''} ${className}`}
      title="Wallet balance"
      aria-label="Wallet balance, add funds"
    >
      {walletQ.isLoading || walletQ.isError ? <span className="text-ps-subtle">-</span> : formatINR(balance)}
    </button>
  );
};

/** Avatar + dropdown with the signed-in email and Sign out. */
const AccountMenu: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { user, logout } = useAuthStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const label = user?.name || user?.email || 'Account';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={`Account, ${label}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`rounded-full grid place-items-center bg-[#6D4AD8] text-white font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-ps-accent ${
          compact ? 'w-11 h-11' : 'w-10 h-10'
        }`}
      >
        {label.charAt(0).toUpperCase()}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-ps-strong bg-ps-card py-1 shadow-xl shadow-black/50">
          <div className="px-4 py-3 border-b border-ps-border">
            {user?.name && <p className="m-0 text-sm font-medium text-ps-text truncate">{user.name}</p>}
            <p className="m-0 text-xs text-ps-subtle truncate">{user?.email}</p>
          </div>
          <button
            type="button"
            onClick={async () => {
              setOpen(false);
              await logout();
              // Hard-redirect to the public landing so we leave the protected
              // area cleanly. Navigating within the SPA can momentarily hit the
              // customer login, which would re-issue the session over SSO.
              window.location.assign('/training/');
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ps-err hover:bg-ps-raised"
          >
            <LogoutIc /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Console brand: Podstack logo + "Podstack Cloud" (same as customer-portal's
 * Logo). The shared ProductSwitcher's trigger is stretched transparently over
 * it, so clicking the brand still opens the product switcher.
 */
const BrandSwitcher: React.FC<{ nameClassName?: string; className?: string }> = ({
  nameClassName = '',
  className = '',
}) => (
  <div className={`relative flex items-center gap-2.5 min-h-[44px] rounded-lg ${className}`}>
    <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="" aria-hidden="true" className="w-7 h-7 flex-none" />
    <span
      aria-hidden="true"
      className={`font-display font-semibold text-[17px] text-ps-text whitespace-nowrap ${nameClassName}`}
    >
      Podstack Cloud
    </span>
    <ProductSwitcher
      subtitle="Training Cloud"
      compact
      className="!absolute inset-0 !p-0 rounded-lg [&>*]:hidden hover:!bg-white/5"
    />
  </div>
);

const openAdvisor =() => window.dispatchEvent(new CustomEvent('podstack-advisor:open'));

const ConsoleLayout: React.FC = () => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the phone menu whenever the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  const mic =
    'w-11 h-11 flex-none grid place-items-center rounded-md border border-ps-strong bg-ps-raised text-ps-text hover:bg-ps-border';

  return (
    <div className="min-h-screen bg-ps-page text-ps-text flex max-[820px]:flex-col">
      {/* Phone bar: brand (product switcher), account, menu toggle */}
      <div className="min-[821px]:hidden flex items-center gap-2 px-4 py-2 bg-ps-chrome border-b border-ps-raised">
        <BrandSwitcher className="min-w-0" />
        <span className="flex-1" />
        <AccountMenu compact />
        <button
          type="button"
          className={mic}
          aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileNavOpen}
          aria-controls="console-nav"
          onClick={() => setMobileNavOpen((o) => !o)}
        >
          {mobileNavOpen ? <CloseIc /> : <MenuIc />}
        </button>
      </div>

      <nav
        id="console-nav"
        aria-label="Primary"
        className={
          'bg-ps-chrome border-ps-raised flex flex-col gap-0.5 overflow-y-auto ' +
          'min-[1101px]:sticky min-[1101px]:top-0 min-[1101px]:h-screen min-[1101px]:w-[232px] min-[1101px]:flex-none min-[1101px]:border-r min-[1101px]:px-3 min-[1101px]:pt-4 min-[1101px]:pb-6 ' +
          `min-[821px]:max-[1100px]:sticky min-[821px]:max-[1100px]:top-0 min-[821px]:max-[1100px]:h-screen min-[821px]:max-[1100px]:w-[76px] min-[821px]:max-[1100px]:flex-none min-[821px]:max-[1100px]:border-r min-[821px]:max-[1100px]:items-center min-[821px]:max-[1100px]:px-2.5 min-[821px]:max-[1100px]:py-4 ` +
          (mobileNavOpen
            ? 'max-[820px]:flex max-[820px]:w-full max-[820px]:border-b max-[820px]:px-4 max-[820px]:pt-2 max-[820px]:pb-4'
            : 'max-[820px]:hidden')
        }
      >
        <BrandSwitcher
          className={`max-[820px]:hidden mb-2 px-2.5 min-[821px]:max-[1100px]:px-0 min-[821px]:max-[1100px]:justify-center min-[821px]:max-[1100px]:w-12`}
          nameClassName={`min-[821px]:max-[1100px]:hidden`}
        />

        {SIDEBAR_NAV.map((g) => (
          <React.Fragment key={g.id}>
            {g.label && (
              <div
                className={`mx-2.5 mt-3.5 mb-0.5 text-[11px] tracking-[0.08em] uppercase text-ps-subtle font-semibold min-[821px]:max-[1100px]:text-[0px] min-[821px]:max-[1100px]:h-px min-[821px]:max-[1100px]:w-8 min-[821px]:max-[1100px]:bg-ps-border min-[821px]:max-[1100px]:mx-0 min-[821px]:max-[1100px]:mt-2.5 min-[821px]:max-[1100px]:mb-1.5`}
              >
                {g.label}
                {g.hint && <span className="normal-case tracking-normal font-normal"> · {g.hint}</span>}
              </div>
            )}
            {g.items.map((item) => {
              const Icon = item.icon;
              return item.external ? (
                <a
                  key={item.id}
                  id={item.id}
                  href={item.href}
                  title={item.name}
                  className={`${itemBase} text-ps-muted hover:bg-ps-hover hover:text-ps-text`}
                >
                  <Icon />
                  <span className={labelCls}>{item.name}</span>
                </a>
              ) : (
                <NavLink
                  key={item.id}
                  id={item.id}
                  to={item.href}
                  title={item.name}
                  className={({ isActive }) =>
                    `${itemBase} ${isActive ? 'bg-ps-accent-soft text-ps-accent' : 'text-ps-muted hover:bg-ps-hover hover:text-ps-text'}`
                  }
                >
                  <Icon />
                  <span className={labelCls}>{item.name}</span>
                </NavLink>
              );
            })}
          </React.Fragment>
        ))}

        {/* Phone: wallet lives in the open menu (the top bar is hidden here) */}
        <div className="min-[821px]:hidden mt-3 flex items-center justify-between gap-3 border-t border-ps-raised pt-3">
          <span className="text-[13px] text-ps-muted">Wallet balance</span>
          <WalletButton className="!min-h-[44px]" onOpen={() => setMobileNavOpen(false)} />
        </div>
      </nav>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar (tablet + desktop) */}
        <header className="max-[820px]:hidden sticky top-0 z-30 flex flex-wrap items-center justify-end gap-2.5 px-7 py-3 border-b border-ps-raised bg-ps-chrome">
          <button type="button" className="ps-btn2" onClick={openAdvisor}>
            <CompassIc />
            <span className="max-[1100px]:hidden">Compute Advisor</span>
            <span className="sr-only min-[1101px]:hidden">Compute Advisor</span>
          </button>
          <WalletButton />
          <AccountMenu />
        </header>

        <main className="flex-1 min-w-0">
          <div className="w-full max-w-[1280px] px-4 pt-5 empty:hidden sm:px-7 sm:pt-7">
            <BuilderCreditsBanner
              onApply={async (data) => {
                await builderCreditsApi.apply(data);
              }}
              kycEnabled={process.env.REACT_APP_ENABLE_BUILDER_CREDITS_KYC === 'true'}
            />
          </div>
          <Outlet />
        </main>
      </div>

      {/* Single wallet modal, available on every console page */}
      <WalletModal />
    </div>
  );
};

export default ConsoleLayout;
